import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { BreadcrumbsItem } from 'react-breadcrumbs-dynamic';
import { UsersService } from 'services/users/users';
import GridContainer from 'components/Grid/GridContainer';
import { dynamicTranslate, translate } from 'components/Translation';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { useDispatch } from 'react-redux';
import { catchError, concatMap, finalize } from 'rxjs/operators';
import { decode } from 'utils/crypto/crypto';
import MobileInternetPlanCard from './subcomponents/MobileInternetPlanCard';
import TariffPlanCard from './subcomponents/TariffPlanCard';
import NumberInformationCard from './subcomponents/NumberInformationCard';
import UserInformationCard from './subcomponents/UserInformationCard';
import AssignUserDialog from './subcomponents/AssignUserDialog';
import { CorpUserData } from 'utils/api/models/users/corpUserData';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import PSTNTable from 'components/ServicesTables/PSTNTable';
import ChangePlanDialog from 'views/Numbers/Details/NumberDetails/subcomponents/ChangePlanDialog';
import CustomConfirmationDialog from 'components/CustomConfirmationDialog/CustomConfirmationDialog';
import { BodyM, HeadingS } from 'components/Typography/Typography';
import { SingleCustomerProduct } from 'utils/api/models/products/singleCustomerProduct';
import { useLocation } from 'react-router';
import BoostPlanCard from './subcomponents/BoostPlanCard';
import FBBTable from 'components/ServicesTables/FBBTable';
import { ECommerceService } from 'services/eCommerce/eCommerce';
import { SinglePackage } from 'utils/api/models/numbers/singlePackage';
import { extractAttributeValue, CustomAttribute } from 'utils/ecommerce/services/extract';
import { EMPTY, throwError } from 'rxjs';
import { MAX_ACCOUNT_CATEGORY, MIN_ACCOUNT_CATEGORY } from 'components/TryAndBuy/constants';
import { CustomerProducts } from 'utils/api/models/products/customerProducts';
import { PostpaidLinesTable } from 'components/ServicesTables/PostpaidLinesTable';
import { CommitmentDetails } from 'utils/api/models/payments/commitmentDetails';
import styles from 'assets/jss/material-dashboard-pro-react/views/numberDetails/detailsStyle';
import { Card, makeStyles } from '@material-ui/core';
import GridItem from 'components/Grid/GridItem';
import InfoBanner from '../common/BandwidthOnDemand/InfoBanner';
import { discount } from 'assets/jss/material-dashboard-pro-react';
import BWODCard from '../common/BandwidthOnDemand/BWODCard';
import { BandwidthOnDemand } from 'services/bandwidthOnDemand/bandwidthOnDemand';
import { BWODExtensionModal } from '../common/BandwidthOnDemand/BWODExtensionModal';
import StatusDialog from 'components/StatusDialog/StatusDialog';
import {
	emailSelector,
	contactNumberSelector,
	accountNameSelector,
	userNameSelector,
	upIdSelector,
	userIdSelector,
	userTypeSelector,
} from 'redux/modules/session/selectors';
import { useSelector } from 'react-redux';
import { FAILURE, LINK_ACTIONS, SUCCESS, status } from '../common/BandwidthOnDemand/constants';
import { isBandwidthOnDemandOn, hideNamaUpgrade } from 'utils/killswitch';
import { remoteConfigSelector } from 'redux/modules/settings/remoteConfig/selectors';
import { getBwodLabels, getCommonLabels, getFiberProLabels } from 'redux/modules/dynamicLabels/selectors';
import idConfig from 'utils/ecommerce/services/id.config';
import { env } from 'config/app.config';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import {
	FIBER_PRO_NOT_AVAILABLE,
	TARIFF_PACKAGE_NAMA,
	numberTypeCopper,
	removeProductIdsKey,
	tarrifPlanIdKey,
} from 'views/Numbers/constants';
import { chain } from 'lodash';
import { TagManager } from 'commons/gtm/tagManager';
import { getLanguage } from 'utils/language/language';
import {
	SingleSubscriberProduct,
	SubscriberDetails,
} from 'utils/api/models/subscriberProducts/singleSubscriberProduct';
import { hasPermission } from 'utils/permissions/permissions';
import { ProductsService } from 'services/products/products';
import { BillingType } from 'utils/api/models/accounts/singleAccount';
import { BasicPlan } from 'utils/api/models/postpaidLines/postpaidLines';
import { customizePlanName, plansToHide } from 'components/ServicesTables/PostpaidLinesTable/constants';
import { loadQualtrics } from 'utils/qualtrics';
import { SubscriberProductsService } from 'services/subscriberProducts/subscriberProducts';
import { ERROR_VALIDATING_TOKEN } from 'utils/api/errors/errorCodes';

// @ts-ignore
const useStyles = makeStyles(styles);
export interface INumberDetailsSubpageProps {
	accountName: string;
	accountCategory: string;
	match: {
		path: string;
		params: {
			phoneNumberEncoded: string;
			accountType: string;
			billingType: string;
			accountNumber: string;
			upAccountId: string;
		};
	};
	isAccountSuspended: boolean;
	isSimCardDisconnected: boolean;
	customerProducts: CustomerProducts;
	tariffPlan: SingleCustomerProduct | undefined;
	tryAndBuyProduct: SingleCustomerProduct | undefined;
	reloadCustomerProducts: () => void;
	commitmentDetails: CommitmentDetails[];
	isAamaliPlus?: boolean;
	hasChangePlanPermission?: boolean;
}

const useQuery = () => {
	return new URLSearchParams(useLocation().search);
};

const NumberDetails = ({
	accountName,
	accountCategory,
	match,
	isAccountSuspended,
	isSimCardDisconnected,
	customerProducts,
	tariffPlan,
	tryAndBuyProduct,
	reloadCustomerProducts,
	commitmentDetails,
	isAamaliPlus,
	hasChangePlanPermission = false,
}: INumberDetailsSubpageProps) => {
	const classes = useStyles();
	const [changePlanDialog, setChangePlanDialog] = useState<{ show: boolean; ind: number; refNo?: string }>({
		show: false,
		ind: -1,
	});
	const [loading, setLoading] = useState<boolean>(false);
	const [bwodLoading, setBwodLoading] = React.useState<boolean>(false);
	const [success, setSuccess] = React.useState(false);
	const [refetchBwod, setRefetchBwod] = React.useState(false);
	const [refNumber, setRefNumber] = React.useState<string>('');
	const [bwodTicketNumber, setBwodTicket] = React.useState<string>('');
	const [extensionModal, openExtensionModal] = React.useState(false);
	const [bwodPlanExtend, setBwodPlanExtend] = React.useState(false);
	const [isStatusModalOpen, setIsStatusModalOpen] = React.useState(false);
	const [corpUserData, setCorpUserData] = useState<CorpUserData | null>(null);
	const [assignUserDialog, setAssignUserDialog] = useState<boolean>(false);
	const [changePlanStatus, setChangePlanStatus] = useState<boolean>(false);
	const [changePlanErrorType, setChangePlanErrorType] = useState<string>('');

	const [loadingSubscriberProducts, setLoadingSubscriberProducts] = useState<boolean>(true);
	const [tryAndBuyPackages, setTryAndBuyPackages] = useState<SinglePackage[]>([]);
	const [planTypeDetails, setPlanTypeDetails] = useState<SubscriberDetails>({});
	const [removeProductIds, setRemoveProductIds] = useState<string[]>();
	const [tarrifPlanId, setTarrifPlanIds] = useState<string>('');

	const [tryAndBuyCatalogEntryView, setTryAndBuyCatalogEntryView] = useState<
		types.ecommerce.CatalogEntryView | undefined
	>(undefined);
	const [bwodresp, setBwodResp] = React.useState<types.bwod.BWODResponse>({
		existing: [],
		recommendation: [],
		expired: [],
	});
	const [bannerText, setBannerText] = useState<{
		description: string;
		link?: string;
		action?: string;
		addonText?: any;
	}>({
		description: '',
	});
	const [bannerBottomText, setBannerBottomText] = useState<string>('');
	const [showBwodCard, setShowBwodCard] = useState<boolean>(false);
	const [showBwodLoading, setShowBwodLoading] = useState<boolean>(false);
	const [currentPlanLoading, setCurrentPlanLoading] = useState<boolean>(false);
	const [isFiberProNumber, setIsFiberProNumber] = useState<boolean>(false);
	const [tarrifPlanBanner, setTarrifPlanbanner] = useState<{ heading?: string; body: string }>({
		heading: '',
		body: '',
	});
	const [isHighestPlanActive, setIsHighestPlanActive] = useState(false);
	const [isProductDetailsLoading, setIsProductDetailsLoading] = useState(false);
	const [isActivePlanACustomizedPlan, setIsActivePlanCustomizedPlan] = useState<boolean>(false);
	const [productFilters, setProductFilters] = useState<string[] | null>(null);
	const [isWrongOtp, setIsWrongOtp] = useState(false);

	let { phoneNumberEncoded, accountNumber, accountType, billingType, upAccountId } = match.params;
	const phoneNumber = decode(phoneNumberEncoded);
	const accountDetails = isAamaliPlus
		? translate('aamali.plus.service')
		: translate(`numbers.assign.user.dialog.${accountType.toLowerCase()}-${billingType.toLowerCase()}`);
	const dispatch = useDispatch();
	const isPostpaid = billingType.toLowerCase() === 'postpaid';
	const isFix: boolean = accountType.toLowerCase() === 'fix';
	const isNAMA: boolean = tariffPlan?.group === TARIFF_PACKAGE_NAMA;
	const isFbb: boolean = accountType.toLowerCase() === 'internet';
	const isBQT: boolean = tariffPlan?.group === 'TARIFF_PACKAGE';
	const isWfbb: boolean = accountType.toLowerCase() === 'wfbb';
	const query = useQuery();
	const openDialog = query.get('open');
	const email = useSelector(emailSelector);
	const contactNumber = useSelector(contactNumberSelector);
	const remoteConfig = useSelector(remoteConfigSelector);
	const fiberProLabels = useSelector(getFiberProLabels);
	const commonLabels = useSelector(getCommonLabels);
	const isBWODOn = isBandwidthOnDemandOn(remoteConfig); // kill switch to show bandwidth on demand card and banners
	const hideNamaUpdate = hideNamaUpgrade(remoteConfig);
	const loggedInUserName = useSelector(userNameSelector);
	const bwodLabels = useSelector(getBwodLabels);
	const userType = useSelector(userTypeSelector);
	const accId = useSelector(upIdSelector);
	const userId = useSelector(userIdSelector);
	const [hiddenPlans, setHiddenPlans] = useState<string[]>([]);
	const [plansToBeHidden, setPlansToBeHidden] = useState<string[]>(plansToHide);
	const [subscriberProducts, setSubscriberProducts] = useState<SingleSubscriberProduct[]>([]);
	const isMounted = useRef(true);
	const fetchCorpUserDepartmentData = useCallback(() => {
		setLoading(true);
		dispatch(showLoadingIndicator());
		return UsersService.getCorpUserDepartmentDataForNumber(upAccountId)
			.pipe(
				finalize(() => {
					dispatch(hideLoadingIndicator());
					setLoading(false);
				})
			)
			.subscribe((userData) => {
				setCorpUserData(userData);
				// dispatch(hideLoadingIndicator());
				setLoading(false);
			});
	}, [upAccountId, dispatch]);

	const activeTryAndBuyProductDuration = useMemo(() => {
		if (tariffPlan) {
			return tryAndBuyPackages.find(
				(singlePackage: SinglePackage) => singlePackage.requiredProducts.indexOf(tariffPlan.productId) >= 0
			)?.duration;
		}
		return undefined;
	}, [tryAndBuyPackages, tariffPlan]);

	const activeCommitments = useMemo(() => {
		if (commitmentDetails && commitmentDetails.length) {
			let commitMentByNumber = commitmentDetails.find((commitment) => commitment.subscriberNo === phoneNumber);
			if (commitMentByNumber) {
				const activeCommitMent = commitMentByNumber.commitmentDetails.find((commitment) => {
					return commitment.status === 'Active';
				});
				if (activeCommitMent) return activeCommitMent;

				return chain(commitMentByNumber.commitmentDetails)
					.filter((element) => element.status === 'Inactive')
					.orderBy(['startDate'], ['desc'])
					.head()
					.value();
			}
		}
	}, [commitmentDetails]);
	const tariffPlanPending = useMemo(() => {
		if (customerProducts?.corpFbbCommitment) {
			return subscriberProducts.some(
				({ status, productId }) =>
					productId === customerProducts?.corpFbbCommitment?.productId && status !== 'Active'
			);
		} else {
			return subscriberProducts.some(
				({ status, productId }) => productId === tariffPlan?.productId && status !== 'Active'
			);
		}
	}, [subscriberProducts, tariffPlan, customerProducts]);

	useEffect(() => {
		if (tariffPlan?.productId) {
			const getContentByPartNumberResponse = ECommerceService.getContentByPartNumber(tariffPlan?.productId).pipe(
				concatMap(({ response }) => {
					if (response.catalogEntryView && response.catalogEntryView.length > 0) {
						const { attributes } = response.catalogEntryView[0];
						const tryAndBuyAttributeValue = extractAttributeValue(
							{ attributes },
							CustomAttribute.TRY_AND_BUY
						);
						if (tryAndBuyAttributeValue)
							return ECommerceService.getContentByPartNumber(tryAndBuyAttributeValue);
					}
					return EMPTY;
				})
			);
			getContentByPartNumberResponse.subscribe(({ response }) => {
				if (response.catalogEntryView && response.catalogEntryView.length > 0) {
					setTryAndBuyCatalogEntryView(response.catalogEntryView[0]);
				}
			});
		}
	}, [tariffPlan]);
	useEffect(() => {
		if (customerProducts?.corpFbbCommitment?.productId) {
			setCurrentPlanLoading(true);
			ECommerceService.getContentByPartNumber(customerProducts?.corpFbbCommitment?.productId)
				.pipe(
					finalize(() => {
						setCurrentPlanLoading(false);
					})
				)
				.subscribe(({ response }) => {
					if (response.catalogEntryView && response.catalogEntryView.length > 0) {
						const { attributes } = response.catalogEntryView[0];

						const isFiberProAttributeValue = extractAttributeValue({ attributes }, CustomAttribute.FBB_PRO);
						setIsFiberProNumber(isFiberProAttributeValue === 'TRUE');
					}
					setCurrentPlanLoading(false);
				});
		}
	}, [customerProducts?.corpFbbCommitment?.productId]);
	useEffect(() => {
		const subscription = fetchCorpUserDepartmentData();
		return () => {
			dispatch(hideLoadingIndicator());
			subscription.unsubscribe();
		};
	}, [fetchCorpUserDepartmentData, dispatch]);

	useEffect(() => {
		if (showBwodLoading) {
			dispatch(showLoadingIndicator());
		} else {
			dispatch(hideLoadingIndicator());
		}
	}, [dispatch]);
	useEffect(() => {
		if (isFix && openDialog === 'changePlanDialog') {
			setChangePlanDialog({ show: true, ind: 1 });
		}
	}, [openDialog, isFix]);

	useEffect(() => {
		const subscription = ECommerceService.getPackages('TRY_AND_BUY', 'FBB').subscribe(({ packages }) => {
			setTryAndBuyPackages(packages);
		});
		return () => {
			subscription.unsubscribe();
		};
	}, []);
	useEffect(() => {
		setLoadingSubscriberProducts(true);
		const subscription = SubscriberProductsService.getSubscriberProducts(phoneNumber, billingType, accountType)
			.pipe(
				finalize(() => {
					if (isMounted.current) {
						setLoadingSubscriberProducts(false);
					}
				})
			)
			.subscribe(({ products, subscriberDetail }) => {
				if (subscriberDetail?.subServiceType === numberTypeCopper) {
					setPlanTypeDetails(subscriberDetail);
					if (products && products.length > 0) {
						let removeIds: string[] = [];
						removeProductIdsKey.map((key) => {
							let getProduct = products.find((product) => product.groupId === key);
							if (getProduct) {
								removeIds.push(getProduct.productId);
							}
							if (key === tarrifPlanIdKey && getProduct) {
								setTarrifPlanIds(getProduct.productId);
							}
						});
						setRemoveProductIds(removeIds);
					}
				}

				setSubscriberProducts(products);
			});
		return () => {
			subscription.unsubscribe();
		};
	}, [phoneNumber, accountType, billingType, tariffPlan]);

	useEffect(() => {
		if (accountNumber && isPostpaid && isNAMA) {
			const currentPlanId = tariffPlan?.productId;
			setLoading(true);
			setIsProductDetailsLoading(true);
			dispatch(showLoadingIndicator());
			ProductsService.getProductsDetails(accountNumber, BillingType.POSTPAID)
				.pipe(
					finalize(() => {
						setLoading(false);
						dispatch(hideLoadingIndicator());
					})
				)
				.subscribe(({ productDetails }) => {
					setLoading(false);
					dispatch(hideLoadingIndicator());
					const currentCommitmentPeriods = commitmentDetails[0]?.commitmentDetails[0]?.totalPeriods || 0;
					const availableTariffPlans = productDetails.tariffPlans.filter((tariffPlan) => {
						const availableCommitment = tariffPlan.planCommitments.find(
							(planCommitment) => +planCommitment.commitmentDuration >= currentCommitmentPeriods
						);
						return !!availableCommitment;
					});

					//check  if highest plan is already active
					const isHighestPlanAvailable = availableTariffPlans.some((products: any) => {
						return products?.basicPlans?.some((product: any) => {
							const currentPlanId = tariffPlan?.productId || undefined;
							const currentProduct = (availableTariffPlans || [])
								.map((tariffPlan: any) => tariffPlan.basicPlans)
								.flat()
								.find((product: any) => product.productId === currentPlanId);
							const currentProductPrice = +currentProduct?.postpaidDetails?.amount;
							const newProductPrice = +product.postpaidDetails.amount;

							return !currentProduct || currentProductPrice < newProductPrice;
						});
					});
					setIsHighestPlanActive(!isHighestPlanAvailable);
					if (!isHighestPlanAvailable) return; // no need of any filterning becasue if highest plan active upgrade button will be disabled

					//logic to create filter for hidden plans in here we added 'Nama 2022' in plansToBeHidden, if we need to hide some othe
					// plans based on this condition like 'Nama 2021' add in plansToBeHidden state
					productDetails.tariffPlans.forEach((plans) => {
						if (plansToBeHidden.includes(plans.name)) {
							const isCurrentPlanHiddenPlan = plans.basicPlans.some((basicPlan) => {
								return basicPlan.productId === currentPlanId;
							});
							if (!isCurrentPlanHiddenPlan) setHiddenPlans([...hiddenPlans, plans.name]);
						}
						// checking if the customer is having the customize plan
						if (plans.name === customizePlanName) {
							setIsActivePlanCustomizedPlan(
								plans?.basicPlans.some((plan) => plan.productId === currentPlanId) || false
							);
						}
					});
					// adding all available plans name in product filters
					setProductFilters(availableTariffPlans.map((tp) => tp.name));
				});
			setIsProductDetailsLoading(false);
		}
	}, [accountNumber, commitmentDetails, tariffPlan?.productId]);

	useEffect(() => {
		//if it's FBB customer then make a call to fetch bandwidth on demand services details
		if (
			((isFbb && isBWODOn) || refetchBwod) &&
			isFiberProNumber &&
			!loadingSubscriberProducts &&
			!tariffPlanPending &&
			planTypeDetails?.subServiceType !== numberTypeCopper &&
			!currentPlanLoading
		) {
			setShowBwodLoading(true);
			BandwidthOnDemand.bandwidthOnDemand({
				subscriberNo: phoneNumber,
				accountType: accountType,
				billingType: billingType,
				planCategory: idConfig.FBB.FBB_FBB_PRO_PLANS[env],
			})
				.pipe(
					catchError((error) => {
						setShowBwodLoading(false);
						return throwError(error);
					})
				)
				.subscribe(({ response }) => {
					setShowBwodLoading(false);
					if (
						!response.data ||
						(response.data &&
							!response.data.recommendation &&
							!response.data.existing &&
							!response.data.expired)
					) {
						setShowBwodCard(false);
					} else {
						//if plan is expired for 2 months then we do not show the card but show the banner
						setBwodResp(response.data);
						setShowBwodCard(true);
						const existingService: types.bwod.IService[] | null = response.data?.existing;
						const expiredService: types.bwod.IService[] | null = response.data?.expired;
						let expiredServiceMonth = expiredService?.find(
							(service) => service.actualDurationMonths === '2' || service.proposedDurationMonths === '2'
						);
						if ((expiredService && expiredService?.length === 2) || expiredServiceMonth) {
							let speed = (expiredService && expiredService[0].downloadSpeed) || '';
							setTarrifPlanbanner({
								heading: bwodLabels.lovedBoost,
								body: dynamicTranslate(bwodLabels.switchToBwodSpeed, speed),
							});
						} else if (
							(existingService && existingService?.length > 0) ||
							(expiredService && expiredService?.length > 0)
						) {
							let speed =
								(existingService && existingService[0].downloadSpeed) ||
								(expiredService && expiredService[0].downloadSpeed) ||
								'';
							setTarrifPlanbanner({
								heading: bwodLabels.lovingBoost,
								body: dynamicTranslate(bwodLabels.upgradeBwodLbl, speed),
							});
						} else {
							setTarrifPlanbanner({
								heading: '',
								body: bwodLabels.tarrifPlanBanner,
							});
						}

						const expiredFor2Months = response.data?.expired?.find(
							(service: any) => service.proposedDurationMonths === '2'
						);
						if (
							(response.data?.expired?.length > 1 || expiredFor2Months) &&
							!response.data.recommendation
						) {
							//if plan is expired for 2 months or expired services has 2 plans then we do not show the card but show the banner
							setBannerText({
								description: bwodLabels.upgradePlanBanner,
								link: bwodLabels.upgradePlan,
								action: LINK_ACTIONS.CHANGE_PLAN,
							});
							setShowBwodCard(false);
						}
					}
				});
		}
	}, [
		isFbb,
		success,
		isFiberProNumber,
		planTypeDetails?.subServiceType,
		tariffPlanPending,
		loadingSubscriberProducts,
		currentPlanLoading,
	]);

	const checkBoostPlanIsVisible = (accountCategory: string) => {
		if (!accountCategory) return false;
		const accountCategoryValue = Number(accountCategory);
		return accountCategoryValue >= MIN_ACCOUNT_CATEGORY && accountCategoryValue <= MAX_ACCOUNT_CATEGORY;
	};

	const isBoostPlanVisible = useMemo(() => checkBoostPlanIsVisible(accountCategory), [accountCategory]);

	const addonSubmitRequest = (numberOfMonths: string, productId: string, productName: string, otp: string) => {
		setBwodLoading(true);
		BandwidthOnDemand.bandwidthOnDemandSubmitRequest({
			subscriberNo: phoneNumber,
			userFullName: loggedInUserName,
			billingAccountNumber: accountNumber,
			bandwidthOnDemandProductId: productId,
			bandwidthOnDemandProductName: productName,
			currentProductId: activeCommitments?.productId,
			monthlyDuration: numberOfMonths,
			emailAddress: email,
			mobileNumber: contactNumber,
			otp,
		}).subscribe(
			({ response }) => {
				setBwodLoading(false);
				reloadCustomerProducts();
				if (response.data) {
					if (response.data.orderId) {
						setSuccess(true);
						setRefNumber(response.data.orderId);
						setIsStatusModalOpen(true);
						openExtensionModal(false);
						setBwodPlanExtend(false);
					} else {
						if (response.data.ticketId) {
							setBwodTicket(response.data.ticketId);
						}
						setSuccess(false);
						openExtensionModal(false);
						setBwodPlanExtend(false);
						setIsStatusModalOpen(true);
					}
				}
			},
			(error) => {
				if (error?.response?.errors[0]?.code === ERROR_VALIDATING_TOKEN) {
					setIsWrongOtp(true);
					setBwodLoading(false);
					return;
				}
				setBwodLoading(false);
				setSuccess(false);
				openExtensionModal(false);
				setBwodPlanExtend(false);
				setIsStatusModalOpen(true);
			}
		);
	};

	useEffect(() => {
		TagManager.dataLayer({
			dataLayer: {
				event: 'My Services',
				step: `My Numbers: Number Details`,
				accountID: accId,
				billingID: accountNumber,
				accountNumber: phoneNumber,
				userId: userId,
				language: getLanguage(),
			},
		});
	}, []);
	const showBandwidthCard = showBwodCard && !loadingSubscriberProducts && !tariffPlanPending;
	return (
		<GridContainer justify="flex-start">
			<BreadcrumbsItem to={match.path}>
				{`${phoneNumber} (${accountName}) - ${translate('path.number.details')}`}
			</BreadcrumbsItem>
			<ConditionalRender
				show={!!bannerText?.description}
				onTrue={() => (
					<InfoBanner
						message={bannerText.description}
						backgroundColor={discount}
						showCloseButton
						linkHandler={() => {
							if (bannerText.action === LINK_ACTIONS.EXTEND_PLAN) {
								openExtensionModal(true);
								setBwodPlanExtend(true);
							} else {
								setChangePlanDialog({ show: true, ind: 1 });
							}
						}}
						handleClose={() => setBannerText({ description: '', link: '', action: '' })}
						link={bannerText.link ? bannerText.link : ''}
						linkPath=""
					/>
				)}
			/>
			<NumberInformationCard
				accountName={accountName}
				accountNumber={accountNumber}
				phoneNumber={phoneNumber}
				corpUserData={corpUserData}
				isSimCardDisconnected={isSimCardDisconnected}
				isAccountSuspended={isAccountSuspended}
			/>
			<UserInformationCard
				corpUserData={corpUserData}
				openAssignUserDialog={() => setAssignUserDialog(true)}
				loadingCorpUserData={loading}
				phoneNumber={phoneNumber}
				fetchCorpUserData={fetchCorpUserDepartmentData}
			/>
			<AssignUserDialog
				isOpen={assignUserDialog}
				setIsOpen={setAssignUserDialog}
				phoneNumber={phoneNumber}
				accountDetails={accountDetails}
				upAccountId={upAccountId}
				corpUserData={corpUserData}
				fetchCorpUserData={fetchCorpUserDepartmentData}
			/>
			<ConditionalRender
				show={showBwodLoading || loadingSubscriberProducts || currentPlanLoading}
				onTrue={() => (
					<div className={classes.bwodLoading}>
						<CustomCircularProgress text={''} loading transparent />
					</div>
				)}
				onFalse={() => (
					<ConditionalRender
						show={!!tariffPlan}
						onTrue={() => (
							<>
								<ConditionalRender
									show={isProductDetailsLoading}
									onTrue={() => <CustomCircularProgress text={''} loading transparent />}
									onFalse={() => (
										<ConditionalRender
											show={
												!!tariffPlan &&
												((isFbb && !!activeCommitments) || !isFbb) &&
												!isProductDetailsLoading
											}
											onTrue={() => (
												<GridItem sm={12} md={12} lg={6}>
													<Card className={classes.planCard}>
														<TariffPlanCard
															onClick={() => setChangePlanDialog({ show: true, ind: 1 })}
															isPostpaid={isPostpaid}
															changePlanAvailable={
																hasChangePlanPermission &&
																(isFix || isFbb || isPostpaid) &&
																!isBQT &&
																(!isNAMA || (!hideNamaUpdate && isNAMA))
															}
															tariffPlan={tariffPlan!}
															tryAndBuyProduct={tryAndBuyProduct}
															corpFbbCommitment={customerProducts.corpFbbCommitment}
															tryAndBuyPlanName={
																tryAndBuyProduct && tryAndBuyCatalogEntryView?.name
															}
															tryAndBuyDuration={activeTryAndBuyProductDuration}
															phoneNumber={phoneNumber}
															billingType={billingType}
															accountType={accountType}
															isAamaliPlus={isAamaliPlus}
															isFbb={isFbb}
															isWfbb={isWfbb}
															showBwodCard={showBandwidthCard}
															commitmentDetails={commitmentDetails}
															activeCommitMents={activeCommitments}
															tarrifPlanBanner={tarrifPlanBanner}
															isHighestPlanActive={isHighestPlanActive}
															setTarrifPlanIds={setTarrifPlanIds}
															tariffPlanPending={tariffPlanPending}
														/>
													</Card>
												</GridItem>
											)}
										/>
									)}
								/>

								<ConditionalRender
									show={showBandwidthCard && !!activeCommitments}
									onTrue={() => (
										<GridItem sm={12} md={12} lg={6}>
											<Card className={classes.planCard}>
												<BWODCard
													bwodresp={bwodresp}
													setBannerText={setBannerText}
													setBannerBottomText={setBannerBottomText}
													openExtensionModal={openExtensionModal}
													setBwodPlanExtend={setBwodPlanExtend}
												/>
												<ConditionalRender
													show={extensionModal}
													onTrue={() => (
														<BWODExtensionModal
															hideModal={openExtensionModal}
															bwodPlanExtend={bwodPlanExtend}
															loading={bwodLoading}
															currentPlan={bwodresp?.currentPlan}
															addOnServices={bwodresp?.recommendation || []}
															submitRequest={addonSubmitRequest}
															activeCommitMents={activeCommitments}
															isWrongOtp={isWrongOtp}
															setIsWrongOtp={setIsWrongOtp}
														/>
													)}
												/>
												<StatusDialog
													isModalOpen={isStatusModalOpen}
													closeModal={() => {
														setIsStatusModalOpen(false);
														setRefetchBwod(true);
													}}
													heading={
														success
															? commonLabels.thankyouHeading
															: commonLabels.failureHeading
													}
													desc={
														success
															? dynamicTranslate(commonLabels.orderIdMsg, refNumber)
															: commonLabels.failureBodyMsg
													}
													ticketId={
														bwodTicketNumber
															? dynamicTranslate(commonLabels.ticketId, bwodTicketNumber)
															: ''
													}
													status={success ? SUCCESS : FAILURE}
													desc2={
														success
															? commonLabels.toFollowRequest
															: bwodTicketNumber
															? ''
															: commonLabels.issuePersistMsg
													}
													footer={success ? commonLabels.contactMsg : ''}
												/>
											</Card>
										</GridItem>
									)}
								/>
							</>
						)}
					/>
				)}
			/>

			<ConditionalRender
				show={isBoostPlanVisible && !tryAndBuyProduct && !isAamaliPlus}
				onTrue={() => (
					<BoostPlanCard
						tariffPlan={tariffPlan}
						phoneNumber={phoneNumber}
						reload={fetchCorpUserDepartmentData}
						tryAndBuyDuration={activeTryAndBuyProductDuration}
						tryAndBuyCatalogEntryView={tryAndBuyCatalogEntryView}
					/>
				)}
			/>
			<ConditionalRender
				show={isPostpaid}
				onTrue={() => (
					<MobileInternetPlanCard
						isPostpaid={isPostpaid}
						phoneNumber={phoneNumber}
						accountType={accountType}
						billingType={billingType}
						customerProducts={customerProducts?.products}
					/>
				)}
			/>
			<ConditionalRender
				show={(isFix || isFbb || isPostpaid) && changePlanDialog.show && !isBQT}
				onTrue={() => (
					<ChangePlanDialog
						hideDialog={() => setChangePlanDialog({ show: false, ind: -1 })}
						TableComponent={isFbb ? FBBTable : isPostpaid && !isFix ? PostpaidLinesTable : PSTNTable}
						planTitle={''}
						partNumber={tariffPlan?.productId}
						phoneNumber={phoneNumber}
						accountType={accountType}
						setChangePlanStatus={setChangePlanStatus}
						displaySuccessDialog={(refNo?: string) =>
							setChangePlanDialog({ show: true, ind: 2, refNo: refNo })
						}
						setChangePlanErrorType={setChangePlanErrorType}
						reloadCustomerProducts={reloadCustomerProducts}
						currentPlanId={isFbb ? tariffPlan?.productId : isPostpaid ? tariffPlan?.productId : undefined}
						corpFbbCommitment={customerProducts.corpFbbCommitment}
						isOpen={changePlanDialog.ind === 1 && changePlanDialog.show}
						accountNumber={accountNumber}
						userName={corpUserData?.userName}
						commitmentDetails={commitmentDetails}
						activeCommitMents={activeCommitments}
						planTypeDetails={planTypeDetails}
						removeProductIds={removeProductIds}
						tarrifPlanId={tarrifPlanId}
						isActivePlanACustomizedPlan={isActivePlanACustomizedPlan}
						productFilters={productFilters}
						hiddenPlans={hiddenPlans}
					/>
				)}
			/>
			<ConditionalRender
				show={changePlanDialog.show && changePlanDialog.ind === 2}
				onTrue={() => (
					<StatusDialog
						isModalOpen={true}
						closeModal={() => {
							setChangePlanDialog({ show: false, ind: -1 });
							loadQualtrics();
						}}
						heading={changePlanStatus ? commonLabels.thankyouHeading : commonLabels.failureHeading}
						desc={
							changePlanStatus
								? fiberProLabels.changePlanSuccMsg
								: changePlanErrorType === FIBER_PRO_NOT_AVAILABLE
								? fiberProLabels.fbbProNotAvailable
								: commonLabels.failureBodyMsg
						}
						status={changePlanStatus ? SUCCESS : FAILURE}
						desc2={
							changePlanStatus
								? commonLabels.toFollowRequest
								: changePlanErrorType === FIBER_PRO_NOT_AVAILABLE
								? ''
								: commonLabels.issuePersistMsg
						}
						footer={changePlanStatus ? commonLabels.contactMsg : ''}
						referenceNumber={changePlanDialog.refNo}
					/>
				)}
			/>
			<ConditionalRender
				show={!!bannerBottomText}
				onTrue={() => <InfoBanner message={bannerBottomText} backgroundColor={discount} />}
			/>
		</GridContainer>
	);
};

export default NumberDetails;
